import { useMemo } from "react";
import { useDataContext } from "../context/DataContext";
import "./Styles/DashboardPage.css";
import { PERSONNEL_COLOR_PALETTE } from "./scheduleColors";

interface UpcomingFlight {
  flightNumber: string;
  carrier: string;
  departAt: Date;
  departTime: string;
  arriveTime: string | null;
  departAirport: string;
  arriveAirport: string;
  daysUntil: number;
}

interface ScheduleEvent {
  AssignmentScheduledEventId?: string | null;
  Abbr?: string | null;
  Colour?: string | null;
  Type?: string | null;
  Hours?: number | null;
  Date?: string | null;
}

interface AppDataAssignment {
  AssignmentId?: string | null;
  JobId?: string | null;
  Job?: {
    JobNumber?: number | string | null;
    Client?: { ClientName?: string | null } | null;
    Site?: { SiteId?: string | null; SiteName?: string | null } | null;
  } | null;
  Schedule?: ScheduleEvent[] | null;
}

interface DayEvent {
  personnelName: string;
  type: string;
  hours: number | null;
  jobNumber: string;
  clientName: string;
  siteName: string;
  color: string;
}

function getLocalDateIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const extractRows = (data: unknown): UnknownRecord[] => {
  if (Array.isArray(data)) return data.filter(isRecord);
  if (isRecord(data)) {
    const possibleArray = data["value"];
    if (Array.isArray(possibleArray)) return possibleArray.filter(isRecord);
    const dataField = data["data"];
    if (Array.isArray(dataField)) return dataField.filter(isRecord);
    if (isRecord(dataField)) {
      const nestedValue = dataField["value"];
      if (Array.isArray(nestedValue)) return nestedValue.filter(isRecord);
    }
  }
  return [];
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
};

function getAirportCode(airport: string | null): string {
  if (!airport || airport.trim() === "") return "TBD";
  const a = airport.toLowerCase();
  if (a.includes("perth")) return "PER";
  if (a.includes("sydney")) return "SYD";
  if (a.includes("melbourne")) return "MEL";
  if (a.includes("brisbane")) return "BNE";
  if (a.includes("adelaide")) return "ADL";
  if (a.includes("darwin")) return "DRW";
  return airport.substring(0, 3).toUpperCase();
}

function formatFlightTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  });
}

function parseNextUpcomingFlight(data: unknown): UpcomingFlight | null {
  const rows = extractRows(data);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const upcomingFlights: UpcomingFlight[] = [];
  
  rows.forEach(booking => {
    const flightData = booking["Flight"];
    if (!isRecord(flightData)) return;
    
    const departAtStr = toStringOrNull(flightData["DepartAt"]);
    if (!departAtStr) return;
    
    const departAt = new Date(departAtStr);
    if (Number.isNaN(departAt.getTime())) return;
    
    // Only include future flights
    const flightDate = new Date(departAt.getFullYear(), departAt.getMonth(), departAt.getDate());
    if (flightDate.getTime() < today.getTime()) return;
    
    const diffTime = flightDate.getTime() - today.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    upcomingFlights.push({
      flightNumber: toStringOrNull(flightData["FlightNumber"]) ?? "—",
      carrier: toStringOrNull(flightData["Carrier"]) ?? "—",
      departAt,
      departTime: formatFlightTime(departAtStr),
      arriveTime: formatFlightTime(toStringOrNull(flightData["ArriveBy"])),
      departAirport: getAirportCode(toStringOrNull(flightData["DepartAirport"])),
      arriveAirport: getAirportCode(toStringOrNull(flightData["ArriveAirport"])),
      daysUntil
    });
  });
  
  // Sort by departure date and return the soonest
  upcomingFlights.sort((a, b) => a.departAt.getTime() - b.departAt.getTime());
  return upcomingFlights[0] ?? null;
}

const PERSONNEL_COLOR = PERSONNEL_COLOR_PALETTE[0];

export default function DashboardPage(): JSX.Element {
  const { personnelAssignmentsMap, personnelNameMap, flightBookingsData, currentPersonnelId } = useDataContext();
  const selectedPersonnelId = currentPersonnelId;
  
  // Get next upcoming flight
  const nextFlight = useMemo(() => {
    if (!flightBookingsData) return null;
    return parseNextUpcomingFlight(flightBookingsData);
  }, [flightBookingsData]);

  // Build today and tomorrow events from cached data for the selected person
  const { todayEvents, tomorrowEvents } = useMemo(() => {
    const now = new Date();
    const todayIso = getLocalDateIso(now);
    
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowIso = getLocalDateIso(tomorrow);

    const todayList: DayEvent[] = [];
    const tomorrowList: DayEvent[] = [];

    if (!selectedPersonnelId) {
      return { todayEvents: todayList, tomorrowEvents: tomorrowList };
    }

    const personnelName = personnelNameMap.get(selectedPersonnelId) ?? "You";
    const personnelData = personnelAssignmentsMap.get(selectedPersonnelId);
    
    if (!personnelData) {
      return { todayEvents: todayList, tomorrowEvents: tomorrowList };
    }

    personnelData.assignments.forEach(raw => {
      const assignment = raw as AppDataAssignment;
      const jobNumber = String(assignment.Job?.JobNumber ?? "—");
      const clientName = assignment.Job?.Client?.ClientName ?? "—";
      const siteName = assignment.Job?.Site?.SiteName ?? "—";
      const scheduleEvents = Array.isArray(assignment.Schedule) ? assignment.Schedule : [];

      scheduleEvents.forEach(evt => {
        const dateValue = evt.Date;
        if (!dateValue) return;
        
        // Extract just the date part (YYYY-MM-DD) from the event date
        const eventDateIso = dateValue.slice(0, 10);
        
        const eventData: DayEvent = {
          personnelName,
          type: evt.Type ?? "—",
          hours: evt.Hours ?? null,
          jobNumber,
          clientName,
          siteName,
          color: PERSONNEL_COLOR
        };

        if (eventDateIso === todayIso) {
          todayList.push(eventData);
        } else if (eventDateIso === tomorrowIso) {
          tomorrowList.push(eventData);
        }
      });
    });

    return { todayEvents: todayList, tomorrowEvents: tomorrowList };
  }, [personnelAssignmentsMap, personnelNameMap, selectedPersonnelId]);

  // Format dates for display
  const todayFormatted = new Date().toLocaleDateString("default", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowFormatted = tomorrowDate.toLocaleDateString("default", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  return (
    <div className="dashboard-page">
      {/* Next Flight Section */}
      {nextFlight && (
        <div className="dashboard-next-flight">
          <div className="dashboard-next-flight__icon">✈️</div>
          <div className="dashboard-next-flight__content">
            <div className="dashboard-next-flight__header">
              <span className="dashboard-next-flight__label">Next Flight</span>
              <span className="dashboard-next-flight__countdown">
                {nextFlight.daysUntil === 0 ? "Today" : 
                 nextFlight.daysUntil === 1 ? "Tomorrow" : 
                 `In ${nextFlight.daysUntil} days`}
              </span>
            </div>
            <div className="dashboard-next-flight__details">
              <span className="dashboard-next-flight__flight-number">{nextFlight.carrier} {nextFlight.flightNumber}</span>
              <span className="dashboard-next-flight__route">
                {nextFlight.departAirport} → {nextFlight.arriveAirport}
              </span>
            </div>
            <div className="dashboard-next-flight__time">
              {nextFlight.departAt.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })} at {nextFlight.departTime}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-schedule-grid">
        {/* Today's Schedule */}
        <div className="dashboard-schedule-card">
          <div className="dashboard-schedule-card__header">
            <h2 className="dashboard-schedule-card__title">Today's Schedule</h2>
            <span className="dashboard-schedule-card__date">{todayFormatted}</span>
          </div>

          <div className="dashboard-schedule-card__content">
            {todayEvents.length === 0 && (
              <div className="dashboard-schedule-card__empty">No scheduled events for today.</div>
            )}
            {todayEvents.length > 0 && (
              <div className="dashboard-schedule-card__list">
                {todayEvents.map((event, idx) => (
                  <div key={idx} className="dashboard-event">
                    <div
                      className="dashboard-event__indicator"
                      style={{ backgroundColor: event.color }}
                    />
                    <div className="dashboard-event__content">
                      <div className="dashboard-event__name">{event.personnelName}</div>
                      <div className="dashboard-event__details">
                        <span className="dashboard-event__type">{event.type}</span>
                        <span className="dashboard-event__hours">{event.hours ?? 0}h</span>
                      </div>
                      <div className="dashboard-event__job">
                        Job #{event.jobNumber} • {event.clientName} • {event.siteName}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tomorrow's Schedule */}
        <div className="dashboard-schedule-card">
          <div className="dashboard-schedule-card__header">
            <h2 className="dashboard-schedule-card__title">Tomorrow's Schedule</h2>
            <span className="dashboard-schedule-card__date">{tomorrowFormatted}</span>
          </div>

          <div className="dashboard-schedule-card__content">
            {tomorrowEvents.length === 0 && (
              <div className="dashboard-schedule-card__empty">No scheduled events for tomorrow.</div>
            )}
            {tomorrowEvents.length > 0 && (
              <div className="dashboard-schedule-card__list">
                {tomorrowEvents.map((event, idx) => (
                  <div key={idx} className="dashboard-event">
                    <div
                      className="dashboard-event__indicator"
                      style={{ backgroundColor: event.color }}
                    />
                    <div className="dashboard-event__content">
                      <div className="dashboard-event__name">{event.personnelName}</div>
                      <div className="dashboard-event__details">
                        <span className="dashboard-event__type">{event.type}</span>
                        <span className="dashboard-event__hours">{event.hours ?? 0}h</span>
                      </div>
                      <div className="dashboard-event__job">
                        Job #{event.jobNumber} • {event.clientName} • {event.siteName}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

